import React, { useState, useEffect } from "react";
import axios from "axios";
import { Percent, Home, Menu } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import ProtectDashboardRoute from "../components/ProtectDashboardRoute";
import config from "../config";

export default function ProfitPercentagePage() {
  const [percentage, setPercentage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const [profitOrders, setProfitOrders] = useState([]);
  const [profitLoading, setProfitLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("authToken");

        if (!token) {
          navigate("/login");
          return;
        }

        const res = await axios.get(`${config.API_BASE_URL}/api/auth/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setProfile(res.data);

        // 🚨 CORRECT: Only admins can access admin pages
        if (res.data.role !== "admin") {
          console.log("❌ Non-admin user trying to access admin page");
          toast.error("Admin access required");
          navigate("/dashboard"); // Redirect to user dashboard, not login
        }
      } catch (err) {
        console.error("❌ Error fetching profile:", err);

        // Handle specific error cases
        if (err.response?.status === 401) {
          toast.error("Session expired. Please login again.");
          localStorage.removeItem("authToken");
        }

        navigate("/login");
      } finally {
        setCheckingAuth(false);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchProfitOrders = async () => {
      try {
        setProfitLoading(true);
        const token = localStorage.getItem("authToken");

        const res = await axios.get(
          `${config.API_BASE_URL}/api/auth/fetchAllOrders`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            // REMOVE: withCredentials: true
          }
        );

        const orders = res.data.orders || [];

        // Filter completed orders
        const completed = orders.filter((o) => o.status === "completed");

        // Extract percentage + profit
        const processed = completed.map((order) => {
          let percentage = 0;

          // If profit stored as a STRING
          if (typeof order.profit === "string") {
            const match = order.profit.match(/percentage:\s*(\d+)/);
            if (match) percentage = Number(match[1]);
          }

          // If profit is normal object
          if (typeof order.profit === "object" && order.profit?.percentage) {
            percentage = order.profit.percentage;
          }

          const profitAmount =
            order.price - order.price / (1 + percentage / 100);

          return {
            ...order,
            percentage,
            profitAmount,
          };
        });

        setProfitOrders(processed);
      } catch (err) {
        console.error("Error loading profits:", err);
      } finally {
        setProfitLoading(false);
      }
    };

    fetchProfitOrders();
  }, []);

  useEffect(() => {
    const fetchPercentage = async () => {
      try {
        const token = localStorage.getItem("authToken");

        const res = await axios.get(
          `${config.API_BASE_URL}/api/admin/get-percentage`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setPercentage(res.data.percentage || 0);
      } catch (err) {
        console.error("Error fetching percentage:", err);
        toast.error("Failed to fetch current percentage");
      }
    };
    fetchPercentage();
  }, []);

  const handleSave = async () => {
    if (percentage === "") {
      toast.error("Please enter a percentage");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");

      await axios.post(
        `${config.API_BASE_URL}/api/admin/set-percentage`,
        { value: Number(percentage) },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("✅ Percentage updated successfully!");
    } catch (err) {
      console.error("Error saving percentage:", err);
      toast.error("❌ Failed to save percentage");
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-600">
        Checking admin access...
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100 text-gray-800 overflow-hidden">
      <ProtectDashboardRoute />
      <Toaster position="top-right" />

      {/* Background overlay for mobile */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
        ></div>
      )}

      {/* Sidebar */}
      <div
        className={`fixed md:static bg-white shadow-lg w-64 p-5 flex flex-col transition-all duration-300 h-full
      ${sidebarOpen ? "left-0" : "-left-64"} md:left-0 z-50`}
      >
        <h1 className="text-2xl font-bold mb-8 text-indigo-600">Admin Panel</h1>

        <nav className="space-y-3">
          <Link
            to="/admin/dashboard"
            className={`flex items-center gap-3 px-4 py-2 rounded-lg w-full transition-all duration-200 ${
              location.pathname === "/admin/dashboard"
                ? "bg-indigo-50 text-indigo-600"
                : "text-gray-700 hover:bg-indigo-50"
            }`}
          >
            <Home className="w-5 h-5" />
            Home
          </Link>

          <Link
            to="/admin/percentage"
            className={`flex items-center gap-3 px-4 py-2 rounded-lg w-full transition-all duration-200 ${
              location.pathname === "/admin/percentage"
                ? "bg-indigo-50 text-indigo-600"
                : "text-gray-700 hover:bg-indigo-50"
            }`}
          >
            <Percent className="w-5 h-5" />
            Profit Percentage
          </Link>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 sm:p-6 md:p-10 overflow-y-auto">
        {/* Mobile menu button */}
        <div className="md:hidden mb-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 bg-indigo-600 text-white rounded-lg"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>

        <h2 className="text-2xl sm:text-3xl font-bold flex items-center gap-2 mb-6 sm:mb-8">
          <Percent className="text-indigo-600" /> Profit Percentage
        </h2>

        {/* Form */}
        <div className="bg-white shadow rounded-xl p-4 sm:p-6 max-w-full sm:max-w-md w-full">
          <label className="block text-gray-700 font-semibold mb-2">
            Set Global Profit Percentage (%)
          </label>
          <input
            type="number"
            value={percentage}
            onChange={(e) => setPercentage(e.target.value)}
            className="w-full border rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-indigo-400"
            placeholder="Enter profit percentage e.g. 10"
          />

          <button
            onClick={handleSave}
            disabled={loading}
            className="mt-4 w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition"
          >
            {loading ? "Saving..." : "Save Percentage"}
          </button>
        </div>

        {/* Profit Table */}
        <div className="mt-10 bg-white shadow rounded-xl p-4 sm:p-6 w-full">
          <h3 className="text-xl sm:text-2xl font-semibold mb-4">
            Completed Order Profits
          </h3>

          {profitLoading ? (
            <p className="text-gray-600">Loading profits...</p>
          ) : profitOrders.length === 0 ? (
            <p className="text-gray-600">No completed orders found.</p>
          ) : (
            <div className="overflow-x-auto w-full">
              <table className="min-w-[700px] w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100 text-sm sm:text-base">
                    <th className="border px-3 sm:px-4 py-2 text-left">
                      Order ID
                    </th>
                    <th className="border px-3 sm:px-4 py-2 text-left">
                      Service
                    </th>
                    <th className="border px-3 sm:px-4 py-2 text-left">
                      Country
                    </th>
                    <th className="border px-3 sm:px-4 py-2 text-left">
                      Amount
                    </th>
                    <th className="border px-3 sm:px-4 py-2 text-left">
                      Percentage
                    </th>
                    <th className="border px-3 sm:px-4 py-2 text-left">
                      Profit
                    </th>
                    <th className="border px-3 sm:px-4 py-2 text-left">Date</th>
                  </tr>
                </thead>

                <tbody>
                  {profitOrders.map((order) => (
                    <tr key={order._id} className="text-sm sm:text-base">
                      <td className="border px-3 sm:px-4 py-2">{order._id}</td>
                      <td className="border px-3 sm:px-4 py-2">
                        {order.serviceName}
                      </td>
                      <td className="border px-3 sm:px-4 py-2">
                        {order.country}
                      </td>
                      <td className="border px-3 sm:px-4 py-2">
                        ₦{order.price.toLocaleString()}
                      </td>
                      <td className="border px-3 sm:px-4 py-2">
                        {order.percentage}%
                      </td>
                      <td className="border px-3 sm:px-4 py-2">
                        ₦{order.profitAmount.toFixed(2)}
                      </td>
                      <td className="border px-3 sm:px-4 py-2">
                        {new Date(order.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
